import { Link, useParams } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { mangas, categories } from '@/mocks/manga';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'rating' | 'views' | 'update'>('rating');

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const categoryName = currentCategory.name;

  const filteredMangas = useMemo(() => {
    let result = slug === 'all' ? [...mangas] : mangas.filter((m) => m.category === slug);
    if (sortBy === 'rating') result.sort((a, b) => b.rating - a.rating);
    if (sortBy === 'views') result.sort((a, b) => b.views - a.views);
    if (sortBy === 'update') result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    return result;
  }, [slug, sortBy]);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.yinghuadongman.com/" },
              { "@type": "ListItem", "position": 2, "name": categoryName, "item": `https://www.yinghuadongman.com/category/${slug}` }
            ]
          })
        }}
      />

      {/* Page Header */}
      <section className="relative pt-24 pb-10 px-4 md:px-6 bg-background-100/50" aria-labelledby="category-title">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-4" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-600">{categoryName}</span>
          </nav>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 id="category-title" className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {categoryName}漫画
              </h1>
              <p className="mt-2 text-sm text-foreground-500">
                共找到 <strong className="text-foreground-800">{filteredMangas.length}</strong> 部{categoryName}韩漫作品
              </p>
            </div>
            <div className="flex items-center gap-1 bg-background-50 border border-background-200/70 rounded-full p-1">
              {[
                { key: 'rating', label: '评分最高', icon: 'ri-star-line' },
                { key: 'views', label: '人气最高', icon: 'ri-fire-line' },
                { key: 'update', label: '最近更新', icon: 'ri-time-line' },
              ].map((s) => (
                <button
                  key={s.key}
                  onClick={() => setSortBy(s.key as typeof sortBy)}
                  className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                    sortBy === s.key
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-500 hover:text-foreground-800'
                  }`}
                >
                  <i className={`${s.icon} text-xs`}></i>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="sticky top-16 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/70" aria-label="分类标签">
        <div className="max-w-7xl mx-auto px-4 md:px-6 overflow-x-auto">
          <div className="flex items-center gap-1 py-3 min-w-max">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap cursor-pointer ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-500 hover:text-foreground-800 hover:bg-background-100'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Manga Grid */}
      <section className="py-8 md:py-12 px-4 md:px-6" aria-label={`${categoryName}漫画列表`}>
        <div className="max-w-7xl mx-auto">
          {filteredMangas.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {filteredMangas.map((manga) => (
                <Link
                  key={manga.id}
                  to={`/manga/${manga.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-3">
                    <img
                      src={manga.coverImage}
                      alt={`${manga.title} 韩漫`}
                      title={`${manga.title} - ${categoryName}韩漫在线阅读`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 text-[10px] font-medium bg-primary-500 text-background-50 rounded-md">{manga.rating}</span>
                    </div>
                    {manga.status === '连载中' && (
                      <div className="absolute top-2 right-2">
                        <span className="px-2 py-0.5 text-[10px] font-medium bg-accent-500 text-background-50 rounded-md">连载</span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors truncate">{manga.title}</h3>
                  <p className="text-xs text-foreground-400 mt-0.5">{manga.altTitle}</p>
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="text-[10px] text-foreground-400">{manga.views.toLocaleString()} 阅读</span>
                    <span className="text-[10px] text-foreground-400">{manga.favorites.toLocaleString()} 收藏</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画作品</p>
              <Link to="/category/all" className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap">查看全部漫画 →</Link>
            </div>
          )}
        </div>
      </section>

      {/* Related Categories */}
      <section className="py-8 md:py-12 px-4 md:px-6 bg-background-100/50" aria-label="相关分类">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">探索更多分类</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {categories.filter((c) => c.slug !== 'all' && c.slug !== slug).slice(0, 8).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-3 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300/50 transition-all cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className={`${cat.icon} text-lg`}></i>
                </div>
                <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">{cat.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}